import { ArrowRight, Minus, Plus, SkipForward } from "lucide-react"
import { observer } from "mobx-react"
import { useState } from "react"
import { DEFAULT_QUEUE_SKIP_MINUTES, queueModeManager } from "./queueModeManager"

const QUEUE_SKIP_OPTIONS = [
    { minutes: 10, label: "10M", description: "10 minutes" },
    { minutes: DEFAULT_QUEUE_SKIP_MINUTES, label: "1H", description: "1 hour" },
    { minutes: 2 * 60, label: "2H", description: "2 hours" },
    { minutes: 4 * 60, label: "4H", description: "4 hours" },
    { minutes: 24 * 60, label: "1D", description: "1 day" },
] as const

export const QueueModeInputControls = observer(function QueueModeInputControls({
    taskId,
    onSkipFor,
    onContinue,
}: {
    taskId: string
    onSkipFor: (minutes: number) => Promise<void>
    onContinue: () => void
}): JSX.Element | null {
    const [skipOptionIndex, setSkipOptionIndex] = useState(1)
    const [pending, setPending] = useState(false)
    const [error, setError] = useState<string | null>(null)
    if (!queueModeManager.isActive) return null

    const skipOption = QUEUE_SKIP_OPTIONS[skipOptionIndex]
    const canSkipCurrentTask = queueModeManager.isTaskEligible(taskId)

    return (
        <div className="flex max-w-full flex-wrap items-center justify-end gap-2" role="group" aria-label="Q mode controls">
            <div
                className="flex shrink-0 items-center bg-base-100 text-base-content"
                role="group"
                aria-label="Skip task"
                title={canSkipCurrentTask ? `Return to this task in ${skipOption.description}` : "Current task is not in the ready queue"}
            >
                <button
                    type="button"
                    className="btn flex h-9 w-8 @max-[600px]:h-11 @max-[600px]:w-11 items-center justify-center text-muted hover:bg-base-300 disabled:cursor-not-allowed disabled:opacity-40"
                    onClick={() => setSkipOptionIndex((index) => Math.max(0, index - 1))}
                    disabled={skipOptionIndex === 0}
                    aria-label="Decrease skip duration"
                    title="Decrease skip duration"
                >
                    <Minus size={14} aria-hidden="true" />
                </button>
                <button
                    type="button"
                    className="btn flex h-9 @max-[600px]:h-11 items-center justify-center gap-2 px-2 text-sm font-medium text-primary hover:bg-base-300 disabled:cursor-not-allowed disabled:opacity-40"
                    onClick={async () => {
                        setPending(true)
                        setError(null)
                        try {
                            await onSkipFor(skipOption.minutes)
                        } catch (error) {
                            setError(error instanceof Error ? error.message : "Unable to put task to sleep")
                        } finally {
                            setPending(false)
                        }
                    }}
                    disabled={!canSkipCurrentTask || pending}
                    aria-label={`Skip current task for ${skipOption.description}`}
                    title={`Skip current task for ${skipOption.description}`}
                >
                    <SkipForward size={14} aria-hidden="true" />
                    <span className="whitespace-nowrap">Skip {skipOption.label}</span>
                </button>
                <button
                    type="button"
                    className="btn flex h-9 w-8 @max-[600px]:h-11 @max-[600px]:w-11 items-center justify-center text-muted hover:bg-base-300 disabled:cursor-not-allowed disabled:opacity-40"
                    onClick={() => setSkipOptionIndex((index) => Math.min(QUEUE_SKIP_OPTIONS.length - 1, index + 1))}
                    disabled={skipOptionIndex === QUEUE_SKIP_OPTIONS.length - 1}
                    aria-label="Increase skip duration"
                    title="Increase skip duration"
                >
                    <Plus size={14} aria-hidden="true" />
                </button>
            </div>
            {error && (
                <span role="alert" className="text-xs text-error">
                    {error}
                </span>
            )}
            <QueueModeContinueButton onContinue={onContinue} disabled={pending || queueModeManager.nextAfterTask(taskId) === null} />
        </div>
    )
})

export const QueueModeContinueButton = observer(function QueueModeContinueButton({
    onContinue,
    disabled,
}: {
    onContinue: () => void
    disabled: boolean
}): JSX.Element | null {
    if (!queueModeManager.isActive) return null

    const waiting = queueModeManager.isWaitingForCapacity
    const title = waiting
        ? "Q mode will continue when a running slot opens"
        : disabled
          ? "Q mode has no other ready tasks"
          : "Continue Q mode with the next ready task"

    return (
        <button
            type="button"
            className="btn flex h-9 shrink-0 items-center justify-center gap-2 bg-primary px-3 text-sm font-medium text-primary-content hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-40 @max-[600px]:h-11"
            onClick={onContinue}
            disabled={disabled || waiting}
            aria-label="Continue Q mode"
            title={title}
        >
            <span>Continue</span>
            <ArrowRight size={14} aria-hidden="true" />
        </button>
    )
})
